import Grid from '@mui/material/Grid';
import { AutoComplete } from '@openx/components-core/lib/AutoComplete/AutoComplete';
import { RadioField } from '@openx/components-core/lib/RadioField/RadioField';
import { TextField } from '@openx/components-core/lib/TextField/TextField';
import type { AppInventoryState } from '@openx/types/targeting/appInventory';
import { DomainTargeting, type DomainTargetingType } from '@openx/types/targeting/domains';
import {
  type CustomBlockingRulesOption,
  CustomBlockingRulesOptions,
  type InventoryAndContentState,
  InventoryContentOption,
  type PublisherInventoryRuleOptions,
} from '@openx/types/targeting/inventoryAndContent';
import { type Dispatch, type SetStateAction, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ID_REGEX } from '../../../shared/InventoryAndContent/constants';
import { publisherInventoryRuleOptionsInitialState } from '../constants';

type CustomBlockingRulesInputsProps = {
  publisherInventoryRuleOptions: PublisherInventoryRuleOptions;
  setPublisherInventoryRuleOptions: Dispatch<SetStateAction<PublisherInventoryRuleOptions>>;
  targetingParams: InventoryAndContentState | AppInventoryState;
};

export const CustomBlockingRulesInputs = ({
  publisherInventoryRuleOptions,
  setPublisherInventoryRuleOptions,
  targetingParams,
}: CustomBlockingRulesInputsProps) => {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);

  const optionsLabels: Record<Partial<CustomBlockingRulesOption>, string> = {
    [CustomBlockingRulesOptions.BUNDLE_ID]: t('App Bundle ID'),
    [CustomBlockingRulesOptions.DOMAIN]: t('Domains'),
  };

  const validatePublisherId = (value: string) => {
    const blockedPublisherIds = targetingParams[InventoryContentOption.PUBLISHER_ID]?.val;
    const isPublisherIdUsed = blockedPublisherIds.includes(value);

    if (isPublisherIdUsed) {
      setError(t('Provided Publisher ID is already blocked.'));
      setPublisherInventoryRuleOptions(ruleOptions => ({
        ...ruleOptions,
        isValid: false,
        publisherID: '',
      }));

      return;
    }

    if (value === '' || !value.match(ID_REGEX)) {
      setError(t('Please enter a valid Publisher ID'));
      setPublisherInventoryRuleOptions(ruleOptions => ({
        ...ruleOptions,
        isValid: false,
        publisherID: '',
      }));

      return;
    }

    setError(null);
    setPublisherInventoryRuleOptions(ruleOptions => ({
      ...ruleOptions,
      isValid: true,
      publisherID: value,
    }));
  };

  const blockingRulesOptions = useMemo(() => Object.values(CustomBlockingRulesOptions), []);
  const isBlockingDomain = useMemo(
    () => publisherInventoryRuleOptions.blockingRuleOption === CustomBlockingRulesOptions.DOMAIN,
    [publisherInventoryRuleOptions.blockingRuleOption]
  );

  // reset state on mount to keep validation disabled
  useEffect(() => {
    setPublisherInventoryRuleOptions(publisherInventoryRuleOptionsInitialState);
  }, [setPublisherInventoryRuleOptions]);

  return (
    <Grid size={12}>
      <TextField
        name="publisher_id"
        placeholder={t('Enter Publisher ID')}
        fullWidth
        label={t('Publisher ID')}
        data-test="custom-blocking-rules-publisher-id-input"
        autoComplete="off"
        sx={{ mt: 1, mb: 1.5 }}
        onChange={e => validatePublisherId(e.target.value)}
        error={!!error}
        helperText={error}
      />

      <AutoComplete
        name="custom_blocking_rules_options"
        textFieldProps={{
          placeholder: t('Select Option'),
          label: t('Pair with'),
        }}
        fullWidth
        data-test="custom-blocking-rules-options-input"
        options={blockingRulesOptions}
        getOptionLabel={option => optionsLabels[option]}
        value={publisherInventoryRuleOptions.blockingRuleOption}
        disableClearable
        onChange={(_, value) =>
          setPublisherInventoryRuleOptions(ruleOptions => ({
            ...ruleOptions,
            blockingRuleOption: value,
          }))
        }
      />

      {isBlockingDomain && (
        <RadioField
          data-test="domain-targeting-options"
          sx={{ mt: 2, '.MuiBox-root': { mb: 1 } }}
          options={[
            {
              label: t('All subdomains with paths'),
              value: DomainTargeting.ALL_VARIATIONS,
              'data-test': 'domain-targeting-all-variations',
            },
            {
              label: t('Root domains with paths'),
              value: DomainTargeting.NO_SUBDOMAINS,
              'data-test': 'domain-targeting-no-subdomains',
            },
          ]}
          onChange={(_, value) =>
            setPublisherInventoryRuleOptions(ruleOptions => ({
              ...ruleOptions,
              domainTargetingOption: value as DomainTargetingType,
            }))
          }
          value={publisherInventoryRuleOptions.domainTargetingOption}
        />
      )}
    </Grid>
  );
};
